import React from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { Select } from '~/components/Select';
import {
    ClinicType,
    PCPType,
    ReminderContext,
    ReminderContextType,
    ServicesContext,
} from '~/services';
import { mapPCPs, mapValueArray } from '~/util';

export function NotificationFilters() {
    const servicesContext = React.useContext(ServicesContext);
    const { clinicList, pcpList } = React.useContext(ReminderContext) as ReminderContextType;
    const [_PCPList, _setPCPList] = React.useState(pcpList);
    const storedFilters = React.useRef<{ clinic: ClinicType[]; pcp: PCPType[] }>({
        clinic: [],
        pcp: [],
    });
    const [clinic, setClinic] = React.useState<ClinicType[]>([]);
    const [pcp, setPCP] = React.useState<PCPType[]>([]);
    const [show, setShow] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    // set default values based on saved filters once clinics are loaded
    React.useEffect(() => {
        const { savedNotificationOptions } = servicesContext.user.getSavedSearchData();
        // clinic and pcp filters in savedNotificationOptions are arrays of IDs
        // so map them to arrays of clinic and pcp objects
        const mappedNotificationFilters = {
            clinic:
                savedNotificationOptions?.filters?.clinic && clinicList
                    ? mapValueArray(savedNotificationOptions?.filters?.clinic, clinicList)
                    : [],
            pcp:
                savedNotificationOptions?.filters?.pcp && pcpList
                    ? mapValueArray(savedNotificationOptions?.filters?.pcp, pcpList)
                    : [],
        };
        storedFilters.current = mappedNotificationFilters;
        setClinic(mappedNotificationFilters.clinic);
        setPCP(mappedNotificationFilters.pcp);
    }, [clinicList, pcpList, servicesContext.user]);

    // Update the PCP list any time the clinic changes
    React.useEffect(() => {
        _setPCPList(mapPCPs(clinic !== null && clinic.length > 0 ? clinic : clinicList));
    }, [clinic, clinicList]);

    async function save() {
        setLoading(true);
        // store the filters as arrays of IDs (values)
        const filters = {
            clinic: clinic?.map((x) => x.value),
            pcp: pcp?.map((x) => x.value),
        };
        await servicesContext.user.setSavedSearchData('notification_options', {
            ...servicesContext.user.getSavedSearchData().savedNotificationOptions,
            filters,
        });
        setLoading(false);
        storedFilters.current = { clinic, pcp };
        setShow(false);
    }

    function cancel() {
        setClinic(storedFilters.current.clinic);
        setPCP(storedFilters.current.pcp);
        _setPCPList(pcpList);
        setShow(false);
    }

    return (
        <div className="row align-items-center">
            <div className="col-3 text-end">
                <div>Filters</div>
            </div>
            <div className="col-6 text-ellipsis">
                {storedFilters.current.clinic.length === 0 && storedFilters.current.pcp.length === 0
                    ? 'None'
                    : [...storedFilters.current.clinic, ...storedFilters.current.pcp]
                          .map((x: ClinicType | PCPType) => x.label)
                          .join(', ')}
            </div>
            <div className="col-3 text-end text-nowrap">
                <button className="btn btn-primary" type="button" onClick={() => setShow(true)}>
                    Filters
                </button>
                <BSModal show={show} onHide={cancel} aria-labelledby="notification-filters-modal">
                    <Modal.Header closeButton>
                        <Modal.Title id="notification-filters-modal" role="heading" aria-level={1}>
                            Notification Filters
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row mb-2">
                            <label htmlFor="clinic-select" className="col-3 col-form-label">
                                Clinic
                            </label>
                            <div className="col-9">
                                <Select
                                    id="clinic-select"
                                    isMulti
                                    options={clinicList as ClinicType[]}
                                    label="Clinic"
                                    placeholder="Any Clinic"
                                    value={clinic}
                                    onChange={(x) => setClinic(x as ClinicType[])}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <label htmlFor="pcp-select" className="col-3 col-form-label">
                                PCP
                            </label>
                            <div className="col-9">
                                <Select
                                    id="pcp-select"
                                    isMulti
                                    options={_PCPList as PCPType[]}
                                    label="PCP"
                                    placeholder="Any PCP"
                                    value={pcp}
                                    onChange={(x) => setPCP(x as PCPType[])}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" className="btn btn-secondary" onClick={cancel}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={save}
                            disabled={loading}
                        >
                            {loading && (
                                <Spinner
                                    animation="border"
                                    className="me-1"
                                    role="status"
                                    size="sm"
                                    variant="light"
                                />
                            )}
                            Save
                        </button>
                    </Modal.Footer>
                </BSModal>
            </div>
        </div>
    );
}
